/**
 * GlassTransmission — real see-through glass on three's physical material
 * (shader additions wave, 2026-09-15; KHR_materials_transmission +
 * KHR_materials_volume).
 *
 * Four things a pane needs, kept separate:
 *   reflection     — Fresnel / BRDF and the surface roughness (unchanged)
 *   direction      — IOR bends the ray (three: `getIBLVolumeRefraction`)
 *   colour loss    — Beer–Lambert along the path through the volume
 *   see-through    — the transmission model, NOT a low alpha
 *
 * three r185 already renders all four for a MeshPhysical(Node)Material with
 * `transmission > 0`: the opaque scene is captured into `viewportMipTexture`
 * and refracted/blurred by roughness and IOR, `thickness` + attenuation give
 * the volume absorption, `dispersion` splits the IOR per channel. This module
 * is the CONTRACT on top: two tiers that stop artists pretending a window is
 * a solid block —
 *   thin   — `thickness` 0: no volume refraction, no absorption; the pane
 *            keeps Fresnel and a roughness-blurred background. Windows.
 *   thick  — an authored thickness in metres, attenuation colour/distance,
 *            optional dispersion. Canopies, visors, vitrines.
 * plus the CPU mirrors the harness pins (Beer–Lambert as `glassTransmittanceCpu` — the nebula owns the
 * `beerLambertCpu` name — and Snell with total
 * internal reflection).
 *
 * LIMITS (inherited from a screen-space capture): no off-screen content, no
 * exact exit point, nested transparent objects are not composed. Converting a
 * standard material to physical is the caller's job (Atlas: `toPhysicalMaterial`).
 */
import * as THREE from 'three/webgpu';

export type GlassTier = 'thin' | 'thick';

export interface GlassConfig {
  tier?: GlassTier;
  /** Index of refraction (default 1.5). */
  ior?: number;
  /** 0..1 (default 1). */
  transmission?: number;
  /** Optional roughness override (blurs the background). */
  roughness?: number;
  /** Thick tier: path length in metres (default 0.05). */
  thicknessM?: number;
  /** Thick tier: the colour the glass tints light to over `attenuationDistanceM`. */
  attenuationColor?: [number, number, number];
  attenuationDistanceM?: number;
  /** Thick tier: chromatic split, 0 = none (default 0). */
  dispersion?: number;
}

export interface V3 { x: number; y: number; z: number }

/** Beer–Lambert: transmittance after `pathLength` when `color` is the tint at `atDistance` (per channel). */
export function glassTransmittanceCpu(color: [number, number, number], atDistance: number, pathLength: number): [number, number, number] {
  const d = Math.max(1e-6, atDistance), l = Math.max(0, pathLength);
  const f = (c: number): number => (c <= 0 ? (l > 0 ? 0 : 1) : Math.pow(Math.min(1, c), l / d));
  return [f(color[0]), f(color[1]), f(color[2])];
}

/**
 * Refracted direction for a unit incident ray I (into the surface), outward
 * normal N and eta = n_incident / n_transmitted; null on total internal reflection.
 */
export function refractDirCpu(i: V3, n: V3, eta: number): V3 | null {
  const cosI = -(i.x * n.x + i.y * n.y + i.z * n.z);
  const k = 1 - eta * eta * (1 - cosI * cosI);
  if (k < 0) return null;
  const s = eta * cosI - Math.sqrt(k);
  return { x: eta * i.x + s * n.x, y: eta * i.y + s * n.y, z: eta * i.z + s * n.z };
}

export function applyGlassTransmission(
  material: THREE.Material & {
    isMeshPhysicalMaterial?: boolean; isMeshPhysicalNodeMaterial?: boolean;
    transmission?: number; ior?: number; thickness?: number; roughness?: number;
    attenuationColor?: THREE.Color; attenuationDistance?: number; dispersion?: number;
    userData: Record<string, unknown>; needsUpdate?: boolean;
  },
  config?: GlassConfig,
): GlassTier | null {
  if (!(material.isMeshPhysicalMaterial || material.isMeshPhysicalNodeMaterial)) return null;
  const tier: GlassTier = config?.tier ?? 'thin';
  material.transmission = config?.transmission ?? 1;
  material.ior = config?.ior ?? 1.5;
  if (config?.roughness !== undefined) material.roughness = config.roughness;
  if (tier === 'thin') {
    material.thickness = 0;                    // thin-walled: no volume, no absorption
    material.attenuationDistance = Infinity;
    material.dispersion = 0;
  } else {
    material.thickness = config?.thicknessM ?? 0.05;
    const c = config?.attenuationColor ?? [0.85, 0.95, 1.0];
    if (material.attenuationColor) material.attenuationColor.setRGB(c[0], c[1], c[2]);
    else material.attenuationColor = new THREE.Color(c[0], c[1], c[2]);
    material.attenuationDistance = config?.attenuationDistanceM ?? 0.5;
    material.dispersion = config?.dispersion ?? 0;
  }
  // Transmission is rendered through three's opaque capture, not alpha blending.
  material.transparent = false;
  material.depthWrite = true;
  material.userData.glass = tier;
  material.needsUpdate = true;
  return tier;
}
